import { DateTime } from 'luxon';
import { validateCPF } from '../../helpers/cpf';

export const generateDeclarationOfAttendanceText = (params: GenerateDeclarationTextParams) => {
  const { endHour, startHour, entranceDate, exitDate, patientName } = params;

  if (entranceDate === exitDate) {
    if (startHour && endHour && startHour !== endHour) {
      return `Declaro para os devidos fins que o(a) paciente ${patientName} esteve em atendimento no período das ${startHour} às ${endHour} do dia ${entranceDate}.`;
    }

    return `Declaro para os devidos fins que o(a) paciente ${patientName} esteve em atendimento no dia ${entranceDate}.`;
  }
  if (startHour && endHour) {
    return `Declaro para os devidos fins que o(a) paciente ${patientName} esteve em atendimento no período de ${startHour} do dia ${entranceDate} a ${endHour} do dia ${exitDate}.`;
  }
  return `Declaro para os devidos fins que o(a) paciente ${patientName} esteve em atendimento, no período de ${entranceDate} a ${exitDate}.`;
};

export const generateCompanionDeclarationOrMedicalCertificateText = (params: GenerateDeclarationTextParams) => {
  const { endHour, startHour, entranceDate, exitDate, patientName } = params;

  if (entranceDate === exitDate) {
    if (startHour && endHour && startHour !== endHour) {
      return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período das ${startHour} às ${endHour} do dia ${entranceDate}, acompanhando o paciente ${patientName}.`;
    }

    return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no dia ${entranceDate}, acompanhando o paciente ${patientName}.`;
  }
  if (startHour && endHour) {
    return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período de ${startHour} do dia ${entranceDate} a ${endHour} do dia ${exitDate}, acompanhando o paciente ${patientName}.`;
  }

  return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período de ${entranceDate} a ${exitDate}, acompanhando o paciente ${patientName}.`;
};

export const generateCompanionMedicalCertificateText = (params: GenerateDeclarationTextParams) => {
  const { endHour, startHour, entranceDate, exitDate, patientName } = params;

  if (entranceDate === exitDate) {
    if (startHour && endHour && startHour !== endHour) {
      return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período das ${startHour} às ${endHour} do dia ${entranceDate}, acompanhando o paciente ${patientName}.`;
    }

    return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no dia ${entranceDate}, acompanhando o paciente ${patientName}.`;
  }
  if (startHour && endHour) {
    return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período de ${startHour} do dia ${entranceDate} a ${endHour} do dia ${exitDate}, acompanhando o paciente ${patientName}.`;
  }

  return `Declaro que o (a) Sr.(a) _________________________ esteve neste serviço de saúde no período de ${entranceDate} a ${exitDate}, acompanhando o paciente ${patientName}.`;
};

interface GenerateDeclarationTextParams {
  startHour: string;
  entranceDate: string;
  endHour: string;
  exitDate: string;
  patientName: string;
}

export enum COMPANION_DOCUMENT_TYPE {
  CPF = 'CPF',
  FOREIGNER = 'FOREIGNER',
  WITHOUT_CPF = 'WITHOUT_CPF',
}

interface GenerateCompanionDeclarationTextParams extends GenerateDeclarationTextParams {
  documentType: COMPANION_DOCUMENT_TYPE;
  companionCpf?: string;
  companionPassport?: string;
  companionBirthDate?: string;
  companionEmissionCountry?: string;
}

const COMPANION_NAME = 'Sr.(a) _________________________';

const describeCompanionDocument = (params: GenerateCompanionDeclarationTextParams) => {
  const { documentType, companionCpf, companionPassport, companionBirthDate, companionEmissionCountry } = params;

  if (documentType === COMPANION_DOCUMENT_TYPE.FOREIGNER) {
    const birthDate = companionBirthDate && DateTime.fromFormat(companionBirthDate, 'dd/MM/yyyy').isValid;

    return [
      birthDate ? `nascido(a) em ${companionBirthDate}` : '',
      companionPassport?.trim() ? `portador(a) do passaporte nº ${companionPassport.trim()}` : '',
      companionEmissionCountry ? `emitido por ${companionEmissionCountry}` : '',
    ]
      .filter(Boolean)
      .join(', ');
  }

  if (documentType === COMPANION_DOCUMENT_TYPE.WITHOUT_CPF) {
    return 'que declarou não possuir inscrição no CPF';
  }

  return companionCpf && validateCPF(companionCpf) ? `inscrito(a) no CPF sob o nº ${companionCpf}` : '';
};

// Parte do texto que o modal já gerava para a declaração e o atestado de acompanhante, com
// período e horários, e só acrescenta o documento depois do nome. Sem documento informado,
// o texto continua exatamente o mesmo de antes.
export const generateCompanionDeclarationText = (params: GenerateCompanionDeclarationTextParams) => {
  const text = generateCompanionDeclarationOrMedicalCertificateText(params);
  const companionDocument = describeCompanionDocument(params);

  if (!companionDocument) return text;

  // Com função, o "$" digitado no passaporte não é lido como padrão de substituição.
  return text.replace(COMPANION_NAME, () => `${COMPANION_NAME}, ${companionDocument},`);
};
